import { getCategoryIcon } from '@/components/ui/transcript-chips/types';
import { cn } from '@/lib/cn';
import { Button, type ButtonSize } from '@heidi/design-system/button';
import { typographyVariant } from '@heidi/design-system/typography';
import type { LucideIcon } from 'lucide-react';
import {
  Brain,
  Download,
  FileText,
  HeartPulse,
  Lightbulb,
  ListChecks,
  MessageCircle,
  PenLine,
  Pill,
  Search,
  SlashSquare,
  Sparkles,
  Stethoscope,
} from 'lucide-react';
import { memo } from 'react';
import type { EvidenceOverlayChip, UseCaseChip } from './evidence-note-overlay.types';

const ICON_MAP: Record<string, LucideIcon> = {
  sparkles: Sparkles,
  'list-checks': ListChecks,
  search: Search,
  brain: Brain,
  lightbulb: Lightbulb,
  stethoscope: Stethoscope,
  'heart-pulse': HeartPulse,
  pill: Pill,
  'pen-line': PenLine,
  'file-text': FileText,
  'message-circle': MessageCircle,
  download: Download,
};

interface UseCaseChipButtonProps {
  chip: UseCaseChip | EvidenceOverlayChip;
  isExpanded?: boolean;
  size?: ButtonSize;
  disabled?: boolean;
  onClick: () => void;
}

export const UseCaseChipButton = memo(
  ({
    chip,
    isExpanded = false,
    size = 'sm',
    disabled = false,
    onClick,
  }: UseCaseChipButtonProps) => {
    const isUseCaseChip = 'icon' in chip;
    const promptCount =
      isUseCaseChip && chip.showPromptCount ? chip.prompts.length : null;
    let IconComponent: LucideIcon;
    if (isUseCaseChip) {
      IconComponent = ICON_MAP[chip.icon] ?? Sparkles;
    } else if (chip.isProtocol) {
      IconComponent = SlashSquare;
    } else {
      IconComponent = getCategoryIcon(chip.category);
    }

    return (
      <Button
        variant="outline"
        size={size}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          'shrink-0 transition-colors duration-200',
          isExpanded
            ? 'bg-background-secondary hover:bg-background-secondary-hover active:bg-background-secondary-pressed'
            : 'bg-surface'
        )}
      >
        <IconComponent className="text-text-secondary" />
        <span className={typographyVariant({ variant: 'p2' })}>{chip.label}</span>
        {promptCount !== null && (
          <span
            data-testid={`chip-prompt-count-${chip.id}`}
            className={cn(
              typographyVariant({ variant: 'footnoteBold' }),
              'rounded-full bg-background-secondary px-1.5 py-0.5 text-text-secondary'
            )}
          >
            {promptCount}
          </span>
        )}
      </Button>
    );
  }
);
UseCaseChipButton.displayName = 'UseCaseChipButton';
